'use client'
import { XIcon } from '@phosphor-icons/react'
import { type JSX, memo, useState } from 'react'
import { useSortBy } from 'react-instantsearch'

import { Button } from '@/components/primitives/Button/Button'
import { Drawer } from '@/components/primitives/Drawer/Drawer'
import { cn } from '@/lib/utils'

export type Facet = { value: string | null; label: string | null }
const SortOptionsWithIndices = [
  { value: 'shopify_products', label: 'Default' },
  { value: 'shopify_products_product_code_asc', label: 'Product Code (A - Z)' },
  { value: 'shopify_products_product_code_desc', label: 'Product Code (Z - A)' },
  { value: 'shopify_products_title_asc', label: 'Title (A - Z)' },
  { value: 'shopify_products_title_desc', label: 'Title (Z - A)' },
]

export const CollectionSortDrawer = memo(function CollectionSortDrawer({ children }: { children: JSX.Element }) {
  const { options, refine, currentRefinement } = useSortBy(
    {
      items: SortOptionsWithIndices,
    },
    { skipSuspense: true },
  )

  const [selectedSort, setSelectedSort] = useState(currentRefinement)

  const applySort = () => {
    refine(selectedSort)
  }

  return (
    <Drawer direction="right" aria-label="Sort">
      <Drawer.Trigger>{children}</Drawer.Trigger>
      <Drawer.Content className={'no-scrollbar relative bottom-0 left-0 h-full overflow-y-auto overflow-x-hidden rounded-l-xl bg-grey-100'}>
        <div className={cn('flex h-full flex-1 flex-col justify-between duration-200')}>
          <Drawer.Header className="relative bg-white px-6 py-5">
            <div className={cn('flex items-center justify-center')}>
              <h3 className="text-lg font-semibold capitalize">Sort</h3>
              <Drawer.Close>
                <XIcon size={24} className="absolute right-6 top-[22px] cursor-pointer text-grey-500" />
              </Drawer.Close>
            </div>
          </Drawer.Header>

          <Drawer.Body className="relative mt-2 flex flex-1 flex-col justify-between overflow-y-auto bg-white p-6">
            <ul className="flex flex-col gap-6">
              {options.map((item) => (
                <li
                  key={item.label}
                  className="flex cursor-pointer justify-between border-b border-grey-200 pb-5 text-base font-medium capitalize"
                  onClick={() => {
                    setSelectedSort((prev) => (prev === item.value ? SortOptionsWithIndices[0].value : item.value))
                  }}
                >
                  {item.label}
                  <div className={cn('flex size-5 items-center justify-center rounded-full border-2 border-grey-300', item.value === selectedSort && 'border-primary-500')}>
                    {item.value === selectedSort && <div className={cn('size-2.5 rounded-full bg-primary-500')} />}
                  </div>
                </li>
              ))}
            </ul>
          </Drawer.Body>
          <Drawer.Footer className="border-t border-t-grey-200 bg-grey-100 px-6 py-5">
            <Drawer.Close>
              <Button variant="solid" className="w-full border-[1px] border-white bg-primary-600 text-white hover:bg-primary-700" onClick={applySort}>
                Apply
              </Button>
            </Drawer.Close>
          </Drawer.Footer>
        </div>
      </Drawer.Content>
    </Drawer>
  )
})
